/** PO org-wide activity notifications (CHP-267).
 *
 * A Programme Owner's Main Dashboard feed shows notifications for ORG activity
 * (in addition to their personal cards) triggered by any BDM or PO in the same
 * organisation. The event set is RESTRICTED — exactly the eight events the
 * ticket names, no more — and every card is ATTRIBUTED to the deal owner's full
 * name so the PO can trace whose activity it is (CHP-267 Fix 2):
 *
 *   1. Deal registration submission           (pending_review, recently submitted)
 *   2. Deal registration outcome              (approved / declined)
 *   3. Deal extension notification made       (extension approved/amended — the
 *                                              owner's registration was extended)
 *   4. Deal extension request made            (extension_pending)
 *   5. Deal extension outcome                 (approved / declined)
 *   6. Closed-Won status sent for approval    (win_pending_review)
 *   7. Closed-Won outcome                     (approved / declined)
 *   8. SPIF earned                            (spif_paid_at recently set)
 *
 * Derived entirely from the org-wide ledger (`getTeamDeals`) the PO already
 * loads — no new fetch. Each event has a RECENCY window so the feed reflects
 * recent org activity, not the entire backlog. Events are informational
 * awareness (lower priority than the PO's actionable team blockers), so the
 * caller weights them below Tier-1/2/3 (see poFeed.ts).
 */

import type { DealOutcomeKind, ManagerDealListItem } from "@/api/client";

/** How recent a state change must be to surface as an org-activity card. Long
 * enough for a PO who checks the dashboard a few times a week to catch it,
 * short enough that the feed reflects RECENT org activity rather than the whole
 * history. Mirrors the manager feed's closed-lost / breach recency windows. */
export const PO_ORG_ACTIVITY_RECENCY_DAYS = 7;

/** The restricted CHP-267 org-activity event set. The `*_submitted` /
 * `*_requested` kinds are in-flight submissions; the `*_outcome_*` kinds are
 * decided. `spif_earned` is the paid-out incentive. */
export type PoOrgActivityKind =
  | "registration_submitted"
  | "registration_approved"
  | "registration_declined"
  | "extension_requested"
  | "extension_approved"
  | "extension_declined"
  | "closed_won_submitted"
  | "closed_won_approved"
  | "closed_won_declined"
  | "spif_earned";

export interface PoOrgActivityItem {
  /** Stable key — deal id + kind (a deal can produce at most one activity card
   * per build, but the kind keeps the key unambiguous across rebuilds). */
  id: string;
  kind: PoOrgActivityKind;
  /** Who the activity is attributed to (the deal owner's full name). May be
   * null when the ledger row carries no owner name. */
  attribution: string | null;
  /** The originating ledger row (client name, display id, etc. for the card). */
  deal: ManagerDealListItem;
  /** The event timestamp (ISO) — drives recency ordering, newest first. */
  occurredAt: string;
}

/** Whether an ISO timestamp is within the recency window of `now`. */
function isRecent(iso: string | null | undefined, now: Date): boolean {
  if (!iso) return false;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return false;
  const ageMs = now.getTime() - ms;
  return ageMs >= 0 && ageMs <= PO_ORG_ACTIVITY_RECENCY_DAYS * 86_400_000;
}

/** Map the persisted outcome discriminator (`last_outcome`) to the org-activity
 * kind for a DECIDED deal, or null when the outcome isn't one CHP-267 surfaces.
 * `registration_assigned` (a manager registered on the owner's behalf) is folded
 * into `registration_approved` — from the org's view a new live deal landed. */
function outcomeKind(last: DealOutcomeKind | null | undefined): PoOrgActivityKind | null {
  switch (last) {
    case "registration_approved":
    case "registration_assigned":
      return "registration_approved";
    case "registration_declined":
      return "registration_declined";
    case "extension_approved":
    case "extension_approved_amended":
      return "extension_approved";
    case "extension_declined":
      return "extension_declined";
    case "closed_won_approved":
      return "closed_won_approved";
    case "closed_won_declined":
      return "closed_won_declined";
    default:
      return null;
  }
}

/**
 * Derive the RESTRICTED org-activity notification for a single ledger row, or
 * null when the deal has no recent CHP-267 event. At most one card per deal —
 * the deal's CURRENT state determines which (a deal in `win_pending_review`
 * surfaces the closed-won submission; once decided it surfaces the outcome).
 *
 * The kind is chosen by current status first (in-flight submissions/requests),
 * then by the persisted outcome on terminal/live states, gated by recency so
 * only recent activity surfaces.
 */
function activityForDeal(deal: ManagerDealListItem, now: Date): PoOrgActivityItem | null {
  const attribution = deal.lead_bdm_name ?? null;
  const make = (
    kind: PoOrgActivityKind,
    occurredAt: string | null | undefined,
  ): PoOrgActivityItem | null =>
    occurredAt ? { id: `${deal.id}-${kind}`, kind, attribution, deal, occurredAt } : null;

  switch (deal.deal_status) {
    case "pending_review":
      // Event 1 — a registration was submitted (still under review).
      return isRecent(deal.submission_timestamp, now)
        ? make("registration_submitted", deal.submission_timestamp)
        : null;
    case "extension_pending":
      // Event 4 — an extension was requested (still under review).
      return isRecent(deal.extension_requested_at, now)
        ? make("extension_requested", deal.extension_requested_at)
        : null;
    case "win_pending_review":
      // Event 6 — closed-won was sent for approval.
      return isRecent(deal.win_submitted_at, now)
        ? make("closed_won_submitted", deal.win_submitted_at)
        : null;
    case "denied": {
      // Event 2 (declined) — only when recently decided.
      const kind = outcomeKind(deal.last_outcome);
      return kind && isRecent(deal.reviewed_at, now) ? make(kind, deal.reviewed_at) : null;
    }
    case "closed_won": {
      // Event 8 — SPIF earned takes precedence (most recent paid event); else
      // event 7 — the closed-won approval.
      if (isRecent(deal.spif_paid_at, now)) return make("spif_earned", deal.spif_paid_at);
      const kind = outcomeKind(deal.last_outcome);
      return kind && isRecent(deal.reviewed_at, now) ? make(kind, deal.reviewed_at) : null;
    }
    case "live": {
      // A LIVE deal carries the most-recent decided outcome: registration
      // approval (events 2), extension notification/outcome (events 3 + 5), or a
      // closed-won rejection back to live (event 7 declined). `last_outcome`
      // already resolves which.
      const kind = outcomeKind(deal.last_outcome);
      return kind && isRecent(deal.reviewed_at, now) ? make(kind, deal.reviewed_at) : null;
    }
    default:
      return null;
  }
}

/**
 * Build the PO's RESTRICTED org-activity notification list (CHP-267) from the
 * org-wide ledger, newest event first.
 *
 * `ownDealIds` are the PO's OWN deals — excluded, because the PO's own activity
 * already surfaces as their personal feed cards (Tier-2); these org cards are
 * for OTHER members' activity. The list is NOT capped here (the feed component
 * slices to the shared max); the caller weights it below the actionable tiers.
 */
export function buildPoOrgActivity(
  teamDeals: ManagerDealListItem[],
  ownDealIds: Set<string>,
  now: Date,
): PoOrgActivityItem[] {
  return teamDeals
    .filter((d) => !ownDealIds.has(d.id))
    .flatMap((d) => {
      const item = activityForDeal(d, now);
      return item ? [item] : [];
    })
    .sort((a, b) => Date.parse(b.occurredAt) - Date.parse(a.occurredAt));
}
